<script src="https://js.stripe.com/v3" async></script>
<script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
<script>
import { mapState, mapActions, mapMutations } from "vuex";
import FooterWrapper from "@/components/FooterWrapper";
import CreditCardElement from "@/components/CreditCardElement";

export default {
  // Included components
  components: {
    FooterWrapper,
    CreditCardElement,
  },

  computed: {
    ...mapState(["teamContracts", "activeTeamId"]),
  },

  data() {
    //Reactive values for credit card inputs
    return {
      paymentScheme: "",
      securityCode: "",
      expirationMonth: "",
      expirationYear: "",
      cardNumber: "",
      linkHandler: {},
      loading: false,
    };
  },
  // Get the most recent contract for the team
  async beforeMount() {
    await this.getTeamContracts();
  },

  async created() {
    // NOTES
    // We essentially copy all of the functional and html code from the PlaidLink.vue
    // file so that we can create a loading page after the linkModal emits success
    // but before the page reroutes
    // for whatever reason, emitting two different events from the PlaidLink Component to the parent,
    // which is this file, did not work and froze the app

    //Create a link token; state action passes teamId to api; Plaid requires some unique identifier
    let linkToken = await this.createLinkToken();
    //Configure plaid link with token, environment, and custom behavior onSuccess, onExit, onLoad (not used)
    let linkHandler = await Plaid.create({
      token: linkToken.data.result.link_token,
      env: process.env.VUE_APP_PLAID_ENV,
      onSuccess: async (public_token, metadata) => {
        this.loading = true;
        //Send the public token to backend to exchange for access_token, to be exchanged for a bank account token and saved to customer
        let response = await this.saveBankAccount({
          public_token: public_token,
          metadata: metadata,
        });
        if (response.ok) {
          await this.route();
        }
      },
      onExit: (err, metadata) => {
        if (err != null) {
          this.pushToast({
            title: "Error",
            message: err.display_message,
            variant: "danger",
          });
        }
      },
    });
    //Set the data attribute linkHandler to the object we just configured so we can call it in the page
    this.linkHandler = linkHandler;
  },

  methods: {
    ...mapActions([
      "getTeamContracts",
      "putContractAsOnboardingUser",
      "createLinkToken",
      "saveBankAccount",
    ]),
    ...mapMutations(["pushToast"]),
    openLink() {
      this.linkHandler.open();
    },

    async route() {
      // On the successful addition of either a bank account or a credit card
      // the Plaid element and Stripe element emit an event, on which this function is called
      // We then need to mark the contract field as true to note the completion of this step
      const success = await this.putContractAsOnboardingUser({
        teamId: this.activeTeamId,
        contractId: this.teamContracts[0].id,
        onboarding_invoice_created:
          this.teamContracts[0].onboarding_invoice_created,
        // Here we set the payment_method_ingested to true
        // This function fires when it receives an event from either the Plaid modal or stripeJS on success
        payment_method_ingested: true,
        pool_info_captured: this.teamContracts[0].pool_info_captured,
      });
      // On completion of this put method, we then need to update the most recent contract to account for this change
      // Then we reroute based on any other incomplete fields
      await this.getTeamContracts();
      if (success) {
        this.loading = false;
        if (!this.teamContracts[0].onboarding_invoice_created) {
          this.$router.push({ path: "/confirm-charge" });
        } else if (!this.teamContracts[0].pool_info_captured) {
          this.$router.push({ path: "/pool-selection" });
        } else {
          this.$router.push({ path: "/" });
        }
      }
    },
  },
};
</script>

<template>
  <div>
    <b-row v-if="loading">
      <b-col class="text-center mt-5">
        <div class="mb-4">
          <b-spinner
            style="width: 4rem; height: 4rem"
            variant="primary"
            label="Loading Workers..."
          ></b-spinner>
        </div>
      </b-col>
    </b-row>
    <div v-if="!loading">
      <FooterWrapper>
        <b-container class="mt-3">
          <h2 align="center">Please Select a Payment Method</h2>
          <div class="spacer" />
          <p align="center">
            <i>
              This payment method will be stored securely in Stripe and used to
              bill you for onboarding and monthly hosting fees
            </i>
          </p>
          <div class="spacer" />
          <b-row align-h="center">
            <b-col lg="4" xl="4" md="4" sm="4" cols="4">
              <div style="align: center">
                <b-button variant="light" @click="openLink">
                  Connect a Bank Account with
                  <img
                    class="icon"
                    src="@/assets/Plaid_(company)-Logo.wine.png"
                  />
                </b-button>
              </div>
            </b-col>
          </b-row>
          <div class="spacer" />
          <div style="text-align: center">
            <p class="or">OR</p>
          </div>
          <b-row align-h="center">
            <b-col md="9" lg="9" xl="9">
              <div class="spacer" />
              <b-form>
                <CreditCardElement @savedCC="route" />
              </b-form>
              <div class="spacer" />
            </b-col>
          </b-row>
        </b-container>
      </FooterWrapper>
    </div>
  </div>
</template>

<style scoped>
.center {
  text-align: center;
}
.spacer {
  height: 25px;
}
.right {
  text-align: right;
}
.or {
  font-size: 20px;
}
.icon {
  height: 60px;
  width: 90px;
}
</style>
